import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Badge } from "@/components/ui/badge";
import { Doc } from "@cvx/_generated/dataModel";
import { useState } from "react";

interface StatusBadgeProps {
  status: string;
  clickable?: boolean;
  video?: Doc<"contractVideos">;
  campaignType?: string;
}

// Status Dialog Component
export const StatusDialog = ({
  video,
  children,
}: {
  video: Doc<"contractVideos">;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getStatusDetails = () => {
    switch (video.status) {
      case "rejected":
        return {
          title: "Video Rejected",
          description: "This video was rejected during review",
          details: [
            { label: "Status", value: "Rejected" },
            {
              label: "AI Rejection Reason",
              value: video.rejectionReason || "No reason provided",
            },
            ...(video.contestReason
              ? [{ label: "Contest Reason", value: video.contestReason }]
              : []),
            ...(video.manualReviewReason
              ? [
                  {
                    label: "Manual Review Decision",
                    value: video.manualReviewReason,
                  },
                ]
              : []),
          ],
        };
      case "approved":
        return {
          title: "Video Approved",
          description: "This video has been approved",
          details: [
            { label: "Status", value: "Approved" },
            ...(video.manualReviewReason
              ? [{ label: "Review Note", value: video.manualReviewReason }]
              : []),
          ],
        };
      case "ai_reviewing":
        return {
          title: "Video Reviewing",
          description: "This video is currently being reviewed.",
          details: [{ label: "Status", value: "AI Reviewing" }],
        };
      default:
        return {
          title: "Video Status",
          description: "Video status information",
          details: [{ label: "Status", value: video.status || "Pending" }],
        };
    }
  };

  const statusInfo = getStatusDetails();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{statusInfo.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {statusInfo.description}
          </p>

          <div className="space-y-3">
            {statusInfo.details.map((detail, index) => (
              <div key={index} className="space-y-1">
                <label className="text-sm font-medium text-primary">
                  {detail.label}
                </label>
                <div className="p-3 rounded-lg bg-secondary border border-border">
                  <p className="text-sm text-primary">{detail.value}</p>
                </div>
              </div>
            ))}
          </div>

          {video.url && (
            <div className="pt-2">
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                View Video →
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main StatusBadge Component
export default function StatusBadge({
  status,
  clickable = false,
  video,
  campaignType,
}: StatusBadgeProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      // Contract statuses
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "pendingApproval":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";
      case "completed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "terminated":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "pending":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";

      // Video statuses - Initial
      case "draft":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";

      // AI Review
      case "ai_reviewing":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400";
      case "ai_approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "ai_rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";

      // Team Review
      case "team_reviewing":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400";
      case "team_rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";
      case "needs_review":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";

      // Brand Review
      case "brand_reviewing":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400";

      // Final statuses
      case "approved":
        return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400";
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400";

      // Deprecated (keep for backwards compatibility)
      case "needs_manual_review":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400";
      case "review":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400";

      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      // Contract statuses
      case "pendingApproval":
        return "Pending Approval";

      // Video statuses - Initial
      case "draft":
        return "Draft";

      // AI Review
      case "ai_reviewing":
        return "🤖 AI Reviewing";
      case "ai_approved":
        return "✅ AI Approved";
      case "ai_rejected":
        return "❌ AI Rejected";

      // Team Review
      case "team_reviewing":
        return "👥 Team Reviewing";
      case "team_rejected":
        return "❌ Rejected";
      case "needs_review":
        return "Needs Review";

      // Brand Review
      case "brand_reviewing":
        return "👤 Brand Reviewing";

      // Final statuses
      case "approved":
        return "✅ Approved";
      case "rejected":
        return "❌ Rejected";

      // Deprecated (keep for backwards compatibility)
      case "needs_manual_review":
        return "👤 Manual Review";
      case "review":
        return "Under Review";

      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const badge = (
    <Badge className={`${getStatusColor(status)} border-0`}>
      {getStatusText(status)}
    </Badge>
  );

  // Only make clickable for challenge campaigns and when video data is available
  if (clickable && campaignType === "challenge" && video) {
    return (
      <StatusDialog video={video}>
        <button className="cursor-pointer hover:opacity-80 transition-opacity">
          {badge}
        </button>
      </StatusDialog>
    );
  }

  return badge;
}
